/**
 * Pure mark-splitting logic of the reader (UX-SPEC §6.2, UX-MAP-05…07).
 *
 * A paragraph's text is cut at every span boundary and each piece is wrapped once; a piece carries the list of spans
 * that cover it. Overlaps therefore never nest marks: the markup depth is one `<mark>` plus at most one wrapper per
 * focus slot (UX-MAP-07). Nothing here touches the DOM or the whole document: callers pass one paragraph's span ids
 * (its own spans plus the spans that reach into it from before, via the view model's cover CSR).
 */
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import type { SpanTable } from '../../shared/viewmodel.ts';

/** The span columns the splitter reads (a `SpanTable` satisfies it). */
export interface SpanCols {
	start: ArrayLike<number>;
	end: ArrayLike<number>;
	channel: ArrayLike<number>;
	weight: ArrayLike<number>;
}

export interface Piece {
	/** Character range [a, b) in the document text. */
	a: number;
	b: number;
	/** Covering span indices, ascending. */
	spans: number[];
	/** Distinct channels whose covering spans have weight > 0 (UX-MAP-05: weight-0 spans count zero). */
	depth: number;
	/** Focus slots present on the piece, as a bitmask (bit 0 = slot 1 … bit 2 = slot 3). 0 in the all-channels layer. */
	slots: number;
}

/** Span indices that touch paragraph `p`: those that start in it, then those that start earlier and reach into it. */
export function spansOfParagraph(
	t: Pick<SpanTable, 'paraFirst' | 'coverStart' | 'coverIdx'>,
	p: number
): number[] {
	const out: number[] = [];
	const c0 = t.coverStart[p] as number;
	const c1 = t.coverStart[p + 1] as number;
	for (let i = c0; i < c1; i++) out.push(t.coverIdx[i] as number);
	const s0 = t.paraFirst[p] as number;
	const s1 = t.paraFirst[p + 1] as number;
	for (let i = s0; i < s1; i++) out.push(i);
	return out;
}

/**
 * Split [a0, b0) at every boundary of the spans in `ids` (clipped to the range) and return the covered pieces in order.
 *
 * `slotOf`, when given, is the focus layer: a Int8Array indexed by channel with the slot 0–2 of a focused channel and
 * −1 otherwise. Only focused channels' spans are considered then, and each piece records its slots; all spans count
 * (weight 0 included), because the focus layer shows a channel's evidence whatever its weight.
 */
export function splitPieces(
	a0: number,
	b0: number,
	ids: readonly number[],
	cols: SpanCols,
	slotOf?: ArrayLike<number> | null
): Piece[] {
	// clip and filter
	const sel: number[] = [];
	const cs: number[] = [];
	const ce: number[] = [];
	for (const id of ids) {
		if (slotOf && (slotOf[cols.channel[id] as number] as number) < 0) continue;
		const s = Math.max(cols.start[id] as number, a0);
		const e = Math.min(cols.end[id] as number, b0);
		if (e <= s) continue;
		sel.push(id);
		cs.push(s);
		ce.push(e);
	}
	const n = sel.length;
	if (n === 0) return [];
	// indices ordered by clipped start
	const byStart = Array.from({ length: n }, (_, i) => i).sort(
		(x, y) => (cs[x] as number) - (cs[y] as number) || x - y
	);

	const out: Piece[] = [];
	let active: number[] = []; // indices into sel
	let si = 0;
	let pos = cs[byStart[0] as number] as number;
	for (;;) {
		while (si < n && (cs[byStart[si] as number] as number) <= pos) {
			active.push(byStart[si] as number);
			si++;
		}
		if (active.length === 0) {
			if (si >= n) break;
			pos = cs[byStart[si] as number] as number;
			continue;
		}
		// the next boundary: the start of a waiting span or the end of an active one
		let next = si < n ? (cs[byStart[si] as number] as number) : Number.POSITIVE_INFINITY;
		for (const k of active) next = Math.min(next, ce[k] as number);
		pushPiece(
			out,
			pos,
			next,
			active.map((k) => sel[k] as number).sort((x, y) => x - y),
			cols,
			slotOf
		);
		pos = next;
		active = active.filter((k) => (ce[k] as number) > pos);
	}
	return out;
}

function pushPiece(
	out: Piece[],
	a: number,
	b: number,
	spans: number[],
	cols: SpanCols,
	slotOf?: ArrayLike<number> | null
): void {
	const prev = out[out.length - 1];
	if (prev && prev.b === a && sameList(prev.spans, spans)) {
		prev.b = b;
		return;
	}
	let depth = 0;
	let slots = 0;
	if (slotOf) {
		for (const id of spans) slots |= 1 << (slotOf[cols.channel[id] as number] as number);
	} else {
		const seen: number[] = [];
		for (const id of spans) {
			if ((cols.weight[id] as number) <= 0) continue;
			const c = cols.channel[id] as number;
			if (!seen.includes(c)) seen.push(c);
		}
		depth = seen.length;
	}
	out.push({ a, b, spans, depth, slots });
}

function sameList(x: readonly number[], y: readonly number[]): boolean {
	if (x.length !== y.length) return false;
	for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
	return true;
}

/** The mark's `d` class for the all-channels layer (UX-MAP-05): 0 for evidence-only, then 1, 2, 3 (3 or more). */
export function depthClass(depth: number): 0 | 1 | 2 | 3 {
	return depth <= 0 ? 0 : depth === 1 ? 1 : depth === 2 ? 2 : 3;
}

/** Channels' slot table for the focus layer: slotOf[channelIndex] = 0, 1, 2 or −1. */
export function slotTable(
	focusChannelIdx: readonly number[],
	channels = CHANNEL_IDS.length
): Int8Array {
	const t = new Int8Array(channels).fill(-1);
	focusChannelIdx.forEach((c, i) => {
		if (i < 3) t[c] = i;
	});
	return t;
}

/** Deepest mark nesting of a piece: the `<mark>` plus one wrapper per focus slot present (UX-MAP-07). */
export function markDepth(piece: Piece): number {
	let n = 0;
	for (let s = 0; s < 3; s++) if (piece.slots & (1 << s)) n++;
	return 1 + n;
}
